import React from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { Button } from '../../ui/Button';
import Link from 'next/link';

interface AdminErrorFallbackProps {
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retry?: () => void;
}

/**
 * Admin-specific error fallback UI
 */
const AdminErrorFallback: React.FC<AdminErrorFallbackProps> = ({ error, retry }) => {
  const handleGoToDashboard = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/admin';
    }
  };

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      // Clear any admin session data and redirect to login
      window.location.href = '/admin/login';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          <div className="text-center">
            {/* Admin Error Icon */}
            <div className="mx-auto h-16 w-16 text-red-600 mb-6">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Admin Panel Error
            </h2>

            <p className="text-gray-600 mb-6 text-sm">
              Something went wrong in the admin panel. This might be a temporary issue.
              If the problem persists, please contact the development team.
            </p>

            {/* Quick Actions */}
            <div className="space-y-3 mb-6">
              {retry && (
                <Button
                  onClick={retry}
                  variant="solid"
                  className="w-full"
                >
                  Try Again
                </Button>
              )}

              <Button
                onClick={handleGoToDashboard}
                variant="secondary"
                className="w-full"
              >
                Go to Dashboard
              </Button>

              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full text-red-600 border-red-300 hover:bg-red-50"
              >
                Logout & Re-login
              </Button>
            </div>

            {/* Admin Resources */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Quick Links:</h3>
              <div className="space-y-2 text-sm">
                <Link
                  href="/admin"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Admin Dashboard
                </Link>
                <Link
                  href="/admin/login"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Admin Login
                </Link>
                <Link
                  href="/"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Public Site
                </Link>
              </div>
            </div>

            {/* Error Details for Development */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-6 text-left bg-gray-50 p-4 rounded text-xs">
                <summary className="cursor-pointer font-medium text-gray-700">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-600">
                  {error.message}
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Contact Information */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p className="mb-1">
                <strong>Need Help?</strong>
              </p>
              <p>
                Contact the development team or check the{' '}
                <a
                  href="/admin/debug"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  debug logs
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Admin Error Boundary Component
 * Specialized for admin panel error handling
 */
export const AdminErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={AdminErrorFallback}
      onError={(error, errorInfo) => {
        // Log admin-specific errors
        console.error('[AdminErrorBoundary] Admin panel error:', error);
        console.error('[AdminErrorBoundary] Error info:', errorInfo);

        // Could send to monitoring service with admin context
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'exception', {
            description: `Admin Error: ${error.message}`,
            fatal: false,
            custom_map: { admin_error: true }
          });
        }
      }}
      errorTitle="Admin Panel Error"
      errorMessage="The admin panel encountered an unexpected error. Please try refreshing or contact support."
      maxRetries={2}
      showErrorDetails={true}
    >
      {children}
    </ErrorBoundary>
  );
};
